import { useState, useEffect, useRef, useCallback } from "react";

// ─── QUESTION DATA ────────────────────────────────────────────────────────────
const QUESTIONS = [
  {
    id: 1,
    text: "According to the Black-Scholes option pricing model, an increase in which of the following variables will cause the price of BOTH calls and puts to rise?",
    options: ["Interest Rates", "Stock Price", "Volatility", "Time to Expiration"],
    correct: 2,
    rationale: "Volatility (Vega) is a positive component for both calls and puts. Higher volatility increases the likelihood of the option finishing deep in-the-money.",
    type: "concept",
  },
  {
    id: 2,
    text: "A stock is trading at $100. A 1-year Call option with a strike of $100 is priced at $10. The risk-free interest rate is 5% and the stock pays no dividends. What is the theoretical price of the 1-year $100 Put using Put-Call Parity?",
    options: ["$5.24", "$5.00", "$10.00", "$15.00"],
    correct: 0,
    rationale: "Put-Call Parity: C − P = S − K/(1+r)^t. 10 − P = 100 − 100/1.05 = 100 − 95.24 = 4.76. Therefore P = 10 − 4.76 = $5.24.",
    type: "numerical",
  },
  {
    id: 3,
    text: "\"Implied Volatility\" is best described as:",
    options: [
      "The standard deviation of the stock's price over the last 30 days.",
      "The volatility figure that, when plugged into an option pricing model, results in the current market price of the option.",
      "The analyst's forecast for the next year's market returns.",
      "The volatility of the overall S&P/TSX 60 Index.",
    ],
    correct: 1,
    rationale: "IV is a \"backward-engineered\" number representing the market's current expectation of future volatility.",
    type: "concept",
  },
  {
    id: 4,
    text: "A trader uses a Binomial Model to price an option with a two-step tree where the stock can move up or down by 10% in each step. Compared to the Black-Scholes model, the Binomial model is better suited for:",
    options: [
      "European-style index options.",
      "American-style options that may be exercised early.",
      "Options on assets with zero volatility.",
      "Long-term government bonds.",
    ],
    correct: 1,
    rationale: "The Binomial model is discrete and allows for the evaluation of exercise value at each node of the tree, making it ideal for American-style options.",
    type: "case",
  },
  {
    id: 5,
    text: "If a stock pays a large dividend, what is the theoretical impact on call and put premiums?",
    options: [
      "Calls increase; Puts decrease.",
      "Calls decrease; Puts increase.",
      "Both increase.",
      "Both decrease.",
    ],
    correct: 1,
    rationale: "Dividends represent a \"leakage\" of value from the stock price. Since call holders don't get the dividend, the call becomes less valuable; conversely, the put becomes more valuable.",
    type: "concept",
  },
  {
    id: 6,
    text: "A 6-month futures contract is trading on a stock priced at $50. The risk-free rate is 4% and there are no dividends or storage costs. What is the Theoretical Fair Value of the future?",
    options: ["$50.00", "$51.00", "$52.00", "$50.99"],
    correct: 1,
    rationale: "F = S × (1 + r × t) = 50 × (1 + 0.04 × 0.5) = 50 × 1.02 = $51.00. (Simple interest approach.)",
    type: "numerical",
  },
  {
    id: 7,
    text: "\"Rho\" measures the sensitivity of an option's price to changes in:",
    options: ["Volatility", "The price of the underlying asset", "Interest rates", "Time decay"],
    correct: 2,
    rationale: "Rho is the Greek that calculates how much a premium changes based on a 1% change in the risk-free rate.",
    type: "concept",
  },
  {
    id: 8,
    text: "In a Delta-Neutral portfolio, if the stock price moves slightly, the portfolio's value remains stable. However, if the stock moves significantly, the portfolio will LOSE value if it has:",
    options: ["Positive Gamma", "Negative Gamma", "Positive Theta", "Negative Rho"],
    correct: 1,
    rationale: "Negative Gamma (common in short option positions) means the Delta becomes unhedged more quickly as the price moves, leading to accelerating losses.",
    type: "hard",
  },
  {
    id: 9,
    text: "The \"Cost of Carry\" for a physical commodity like Crude Oil includes all of the following EXCEPT:",
    options: [
      "Storage costs",
      "Insurance costs",
      "Foregone interest on the capital used to buy the oil",
      "The daily trading commission of the broker",
    ],
    correct: 3,
    rationale: "Commissions are transaction costs, not \"carrying\" costs associated with holding the physical asset over time.",
    type: "concept",
  },
  {
    id: 10,
    text: "An option has a Delta of 0.50 and a Gamma of 0.10. If the underlying stock rises by $1.00, what is the new Delta?",
    options: ["0.50", "0.60", "0.10", "0.40"],
    correct: 1,
    rationale: "New Delta = Old Delta + (Gamma × Change in Stock) = 0.50 + (0.10 × 1) = 0.60.",
    type: "numerical",
  },
  {
    id: 11,
    text: "Which of the following variables in the Black-Scholes model is the only one that is NOT directly observable in the market?",
    options: ["Strike Price", "Time to Expiration", "Risk-free Interest Rate", "Volatility"],
    correct: 3,
    rationale: "Strike, Time, and Interest rates are known facts. Volatility must be estimated (Historical) or implied (IV).",
    type: "concept",
  },
  {
    id: 12,
    text: "Put-Call Parity describes a relationship that, if violated, allows a trader to engage in:",
    options: ["Hedging", "Arbitrage", "Speculation", "Short-selling"],
    correct: 1,
    rationale: "If the synthetic price and the market price differ, a risk-free profit can be made by buying one and selling the other.",
    type: "concept",
  },
  {
    id: 13,
    text: "An investor notices that as interest rates rise, the price of the call options they hold also increases. This is because:",
    options: [
      "Higher rates make it more expensive to buy stock on margin, making the call a more attractive alternative.",
      "Higher rates decrease the present value of the strike price (the future payment).",
      "Higher rates always lead to higher stock prices.",
      "Both A and B are correct.",
    ],
    correct: 3,
    rationale: "Rising rates increase the Cost of Carry for the underlying stock, which increases Call values (Positive Rho).",
    type: "case",
  },
  {
    id: 14,
    text: "An option is said to be \"Fairly Valued\" when:",
    options: [
      "Its market price equals its intrinsic value.",
      "Its market price equals its theoretical value derived from a pricing model.",
      "The Delta is exactly 0.50.",
      "It is at-the-money.",
    ],
    correct: 1,
    rationale: "Theoretical value is the benchmark for what an option should cost.",
    type: "concept",
  },
  {
    id: 15,
    text: "An option has a Vega of 0.20. If the Implied Volatility rises from 25% to 28%, what is the expected change in the option's premium?",
    options: ["$0.20 increase", "$0.60 increase", "$0.60 decrease", "$0.20 decrease"],
    correct: 1,
    rationale: "Change = Vega × (Change in IV) = 0.20 × (28 − 25) = 0.20 × 3 = $0.60.",
    type: "numerical",
  },
  {
    id: 16,
    text: "The \"Time Decay\" of an option is non-linear, meaning:",
    options: [
      "It stays the same every day.",
      "It accelerates as the option approaches the expiration date.",
      "It slows down as the option approaches expiration.",
      "It only happens on weekends.",
    ],
    correct: 1,
    rationale: "The Theta curve steepens significantly in the final 30 days of an option's life.",
    type: "concept",
  },
  {
    id: 17,
    text: "A \"Risk-Neutral\" valuation assumes that:",
    options: [
      "No investors care about risk.",
      "The expected return on the underlying asset is the risk-free rate.",
      "The stock price can never fall.",
      "All options are fairly priced at zero.",
    ],
    correct: 1,
    rationale: "This is a core assumption of the Binomial and Black-Scholes models to simplify the math of discounting future payoffs.",
    type: "hard",
  },
  {
    id: 18,
    text: "\"Arbitrage\" is defined as the simultaneous purchase and sale of an asset to profit from:",
    options: [
      "Future price increases.",
      "A price difference between two markets or instruments.",
      "High volatility.",
      "Low interest rates.",
    ],
    correct: 1,
    rationale: "Arbitrageurs provide market efficiency by closing gaps in pricing.",
    type: "concept",
  },
  {
    id: 19,
    text: "If the 3-month risk-free rate is 2% (annualized) and the spot price of gold is $2,000, what is the \"Interest\" component of the cost of carry for a 3-month future?",
    options: ["$10", "$40", "$20", "$5"],
    correct: 0,
    rationale: "Interest = Spot × Rate × Time = $2,000 × 0.02 × (3/12) = $2,000 × 0.005 = $10.",
    type: "numerical",
  },
  {
    id: 20,
    text: "When the \"Implied Volatility\" is higher than the \"Historical Volatility,\" it suggests that the market:",
    options: [
      "Is overvalued.",
      "Expects the stock to be more volatile in the future than it has been in the past.",
      "Is bearish on the stock.",
      "Is about to close.",
    ],
    correct: 1,
    rationale: "IV is forward-looking; HV is backward-looking.",
    type: "concept",
  },
  {
    id: 21,
    text: "A trader notices that an option's Delta is 1.00. This implies that the option is:",
    options: [
      "At-the-money.",
      "Deep out-of-the-money.",
      "Deep in-the-money.",
      "About to expire worthless.",
    ],
    correct: 2,
    rationale: "A Delta of 1.00 means the option moves dollar-for-dollar with the stock, which only happens when there is almost 100% certainty it will finish ITM.",
    type: "case",
  },
  {
    id: 22,
    text: "Which Greek measures the change in an option's premium for a one-day passage of time?",
    options: ["Delta", "Gamma", "Theta", "Vega"],
    correct: 2,
    rationale: "Theta is the \"time decay\" Greek.",
    type: "concept",
  },
  {
    id: 23,
    text: "In the Black-Scholes model, if the \"Time to Expiration\" increases, the value of a European Call option (with no dividends):",
    options: [
      "Always increases.",
      "Always decreases.",
      "Increases only if the option is in-the-money.",
      "May increase or decrease depending on dividends.",
    ],
    correct: 0,
    rationale: "More time gives more opportunity for the stock price to rise above the strike.",
    type: "hard",
  },
  {
    id: 24,
    text: "The \"Spot Market\" is also known as the:",
    options: ["Futures Market", "Cash Market", "Options Market", "Secondary Market"],
    correct: 1,
    rationale: "It is the market for immediate delivery of the actual asset.",
    type: "concept",
  },
  {
    id: 25,
    text: "A stock is at $45. A $40 Put is trading at $0.50. What is the \"Intrinsic Value\"?",
    options: ["$5.00", "$0.50", "$0.00", "-$5.00"],
    correct: 2,
    rationale: "Intrinsic Value for a Put = Strike − Stock = 40 − 45 = −$5. Since intrinsic value cannot be negative, it is $0.",
    type: "numerical",
  },
  {
    id: 26,
    text: "\"Put-Call Parity\" applies to which style of options?",
    options: [
      "American-style only.",
      "European-style only.",
      "Both American and European.",
      "Only index options.",
    ],
    correct: 1,
    rationale: "The math of the parity formula relies on the fact that the options cannot be exercised until the expiration date.",
    type: "concept",
  },
  {
    id: 27,
    text: "If a trader is \"Long Gamma,\" they will benefit from:",
    options: [
      "Stable prices and time decay.",
      "Large moves in the underlying stock price.",
      "Falling volatility.",
      "Rising interest rates.",
    ],
    correct: 1,
    rationale: "Positive Gamma means your Delta increases as the stock moves in your favor, and decreases as it moves against you.",
    type: "hard",
  },
  {
    id: 28,
    text: "An option has a Theta of −0.05. If the current premium is $3.00, what will the premium be tomorrow, all other factors held constant?",
    options: ["$3.05", "$3.00", "$2.95", "$2.50"],
    correct: 2,
    rationale: "New Premium = Old Premium + Theta = $3.00 − 0.05 = $2.95.",
    type: "numerical",
  },
  {
    id: 29,
    text: "The \"Exercise Price\" is also known as the:",
    options: ["Spot Price", "Strike Price", "Premium", "Basis"],
    correct: 1,
    rationale: "Standardized terminology in derivatives contracts.",
    type: "concept",
  },
  {
    id: 30,
    text: "Which factor has the LEAST impact on the price of a short-term (1-week) equity option?",
    options: ["Stock Price", "Volatility", "Interest Rates (Rho)", "Time to Expiration"],
    correct: 2,
    rationale: "Rho is generally the smallest of the Greeks and requires significant time to have a meaningful impact on the premium.",
    type: "concept",
  },
  {
    id: 31,
    text: "In the \"Cost of Carry\" model for index futures, the \"Income\" component refers to:",
    options: [
      "The capital gains of the index.",
      "The dividends paid by the companies within the index.",
      "The commissions earned by the exchange.",
      "The interest earned on the margin account.",
    ],
    correct: 1,
    rationale: "The cost of carry for an index is the risk-free rate minus the dividend yield.",
    type: "concept",
  },
  {
    id: 32,
    text: "A stock is at $60. A 3-month Call with a strike of $60 is trading at $4.00. The risk-free rate is 4%. Using Put-Call Parity, what is the theoretical price of the $60 Put? (No dividends)",
    options: ["$3.20", "$3.40", "$4.00", "$4.60"],
    correct: 1,
    rationale: "4.00 − P = 60 − 60/(1 + 0.04×0.25) = 60 − 60/1.01 = 60 − 59.40 = 0.60. P = 4.00 − 0.60 = $3.40.",
    type: "numerical",
  },
  {
    id: 33,
    text: "If a market moves from \"Contango\" to \"Backwardation,\" what has happened?",
    options: [
      "The futures price has become significantly higher than the spot.",
      "The spot price has risen above the futures price.",
      "Both prices have fallen to zero.",
      "The volatility has decreased.",
    ],
    correct: 1,
    rationale: "Backwardation occurs when the spot price exceeds the futures price, often due to high immediate demand for the physical asset.",
    type: "concept",
  },
  {
    id: 34,
    text: "An investor holds a Deep In-the-Money (DITM) Put option. As the stock price continues to fall toward zero, what happens to the option's Delta?",
    options: [
      "It moves from −0.50 toward 0.00.",
      "It moves from −0.90 toward −1.00.",
      "It moves from 1.00 toward 0.00.",
      "It stays constant at −0.50.",
    ],
    correct: 1,
    rationale: "As a put becomes DITM, it begins to move dollar-for-dollar in the opposite direction of the stock, meaning its Delta approaches −1.00.",
    type: "case",
  },
  {
    id: 35,
    text: "Which of the following describes \"Vega\" risk?",
    options: [
      "The risk that interest rates will rise.",
      "The risk that time decay will accelerate.",
      "The risk that a change in implied volatility will impact the option premium.",
      "The risk that the stock will undergo a split.",
    ],
    correct: 2,
    rationale: "Vega measures the change in an option's price for a 1% change in implied volatility.",
    type: "concept",
  },
  {
    id: 36,
    text: "An option has a Gamma of 0.05. If the stock price falls by $2.00, what is the impact on the option's Delta?",
    options: [
      "The Delta increases by 0.10.",
      "The Delta decreases by 0.10.",
      "The Delta stays the same.",
      "The Delta increases by 0.05.",
    ],
    correct: 1,
    rationale: "Change in Delta = Gamma × Change in Stock = 0.05 × (−2.00) = −0.10.",
    type: "numerical",
  },
  {
    id: 37,
    text: "\"Theoretical Fair Value\" of a future is the price at which:",
    options: [
      "All traders make a profit.",
      "There is no opportunity for risk-free arbitrage.",
      "The volatility is at its 10-year average.",
      "The Montreal Exchange sets the opening bell.",
    ],
    correct: 1,
    rationale: "Fair value is the equilibrium point where the cost of buying the spot and carrying it equals the futures price.",
    type: "concept",
  },
  {
    id: 38,
    text: "According to the Black-Scholes model, if the \"Risk-Free Rate\" increases, the value of a Put Option will:",
    options: ["Increase.", "Decrease.", "Remain the same.", "Become extremely volatile."],
    correct: 1,
    rationale: "Higher interest rates decrease the present value of the strike price (the money received in the future for a put), making the put less valuable. (Negative Rho for puts.)",
    type: "hard",
  },
  {
    id: 39,
    text: "\"Historical Volatility\" is calculated using:",
    options: [
      "The current market price of the option.",
      "The standard deviation of past price returns of the underlying asset.",
      "The dividend yield of the stock.",
      "The risk-free rate of return.",
    ],
    correct: 1,
    rationale: "HV is a statistical measure of how much the stock actually moved in the past.",
    type: "concept",
  },
  {
    id: 40,
    text: "A 3-month futures contract has a Theoretical Fair Value of $102.50 but is trading at $104.00. An arbitrageur should:",
    options: [
      "Buy the future and sell the spot.",
      "Sell the future and buy the spot.",
      "Buy both instruments.",
      "Do nothing, as the difference is too small.",
    ],
    correct: 1,
    rationale: "The future is overvalued relative to fair value. Selling the expensive future and buying the cheaper spot locks in a risk-free profit.",
    type: "numerical",
  },
  {
    id: 41,
    text: "In an Efficient Market, Implied Volatility should theoretically reflect:",
    options: [
      "Only the stock's performance over the last year.",
      "All publicly available information about the future uncertainty of the stock.",
      "The RR's personal opinion.",
      "The highest price the stock reached today.",
    ],
    correct: 1,
    rationale: "Efficiency assumes all known factors regarding risk are priced into the option's premium.",
    type: "concept",
  },
  {
    id: 42,
    text: "An option has a Delta of 0.50. If the stock rises by $1.00, the premium rises by ~$0.50. If the stock then falls back $1.00, why might the premium NOT return to its original price?",
    options: [
      "Because of the impact of Theta (time decay).",
      "Because Gamma changed the Delta during the move.",
      "Because Implied Volatility might have shifted.",
      "All of the above.",
    ],
    correct: 3,
    rationale: "Option pricing is dynamic; time, rate of change (Gamma), and volatility all influence the price simultaneously.",
    type: "case",
  },
  {
    id: 43,
    text: "Which factor in option pricing is usually considered to have a \"Linear\" relationship with the premium in the very short term?",
    options: [
      "Time to Expiration.",
      "Volatility.",
      "Underlying Stock Price (as measured by Delta).",
      "Interest Rates.",
    ],
    correct: 2,
    rationale: "Delta provides a linear approximation of price change, though Gamma eventually introduces curvature (non-linearity).",
    type: "concept",
  },
  {
    id: 44,
    text: "A stock is at $50, strike is $50, risk-free rate is 5%. A 1-year Call is $7.00 and a 1-year Put is $4.62. Is this market in equilibrium per Put-Call Parity?",
    options: [
      "Yes.",
      "No, the Call is too cheap.",
      "No, the Put is too cheap.",
      "No, the stock price should be higher.",
    ],
    correct: 0,
    rationale: "C − P = 7.00 − 4.62 = 2.38. S − K/(1+r) = 50 − 50/1.05 = 50 − 47.62 = 2.38. Since 2.38 = 2.38, the market is in equilibrium.",
    type: "numerical",
  },
  {
    id: 45,
    text: "\"Negative Theta\" is a term used to describe:",
    options: [
      "An option that is gaining value over time.",
      "The \"cost\" of being long an option as time passes.",
      "A market where interest rates are below zero.",
      "A stock that is delisted.",
    ],
    correct: 1,
    rationale: "Long option positions have negative Theta because they lose value (bleed) every day.",
    type: "concept",
  },
  {
    id: 46,
    text: "\"Vega\" is highest for options that are:",
    options: [
      "Deeply In-the-Money.",
      "Deeply Out-of-the-Money.",
      "At-the-Money.",
      "Expiring today.",
    ],
    correct: 2,
    rationale: "ATM options are most sensitive to changes in volatility because their value is entirely composed of extrinsic (time/vol) value.",
    type: "concept",
  },
  {
    id: 47,
    text: "If an investor sells a \"Straddle,\" they are effectively:",
    options: [
      "Long Volatility and Short Theta.",
      "Short Volatility and Long Theta.",
      "Long Gamma and Long Vega.",
      "Short Rho and Long Delta.",
    ],
    correct: 1,
    rationale: "Selling a straddle means you want volatility to drop (Short Vega) and you want time to pass so you keep the premium (Long Theta).",
    type: "hard",
  },
  {
    id: 48,
    text: "An option has a Rho of 0.10. If interest rates rise by 1.5%, what is the expected change in the call premium?",
    options: ["$0.10 increase", "$0.15 increase", "$1.50 increase", "$0.10 decrease"],
    correct: 1,
    rationale: "Change = Rho × Change in Rate = 0.10 × 1.5 = $0.15.",
    type: "numerical",
  },
  {
    id: 49,
    text: "\"Gamma\" is often compared to which physical concept?",
    options: ["Speed.", "Friction.", "Acceleration.", "Gravity."],
    correct: 2,
    rationale: "If Delta is speed (rate of change), Gamma is acceleration (the rate at which that speed changes).",
    type: "concept",
  },
  {
    id: 50,
    text: "An investor notices that the Implied Volatility of OTM Puts is significantly higher than OTM Calls for the same stock. This phenomenon is known as:",
    options: [
      "The Volatility Smile.",
      "The Volatility Skew (or Smirk).",
      "Backwardation.",
      "Put-Call Parity.",
    ],
    correct: 1,
    rationale: "Skew often occurs because investors are more willing to pay a premium for downside protection (puts) than upside speculation (calls).",
    type: "case",
  },
  {
    id: 51,
    text: "Which of the following is a \"Discrete\" pricing model?",
    options: ["Black-Scholes.", "Binomial.", "Monte Carlo.", "Geometric Brownian Motion."],
    correct: 1,
    rationale: "Binomial models break time into specific, separate steps or nodes.",
    type: "concept",
  },
  {
    id: 52,
    text: "A client is Long a call with a Delta of 0.60. They want to be Delta Neutral. To achieve this, they should:",
    options: [
      "Buy 60 shares of stock.",
      "Sell 60 shares of stock.",
      "Buy a put with a 0.40 Delta.",
      "Sell a call with a 0.60 Delta.",
    ],
    correct: 1,
    rationale: "+0.60 (Call) and −0.60 (Shorting 60 shares) equals a Net Delta of zero.",
    type: "numerical",
  },
  {
    id: 53,
    text: "\"Theoretical Value\" assumes that the market is:",
    options: [
      "Highly volatile.",
      "Frictionless (no taxes or commissions).",
      "Controlled by institutional investors.",
      "Always in backwardation.",
    ],
    correct: 1,
    rationale: "Models simplify reality by removing transaction costs to find the pure mathematical value.",
    type: "concept",
  },
  {
    id: 54,
    text: "If a stock undergoes a \"Reverse Split\", how is the Strike Price adjusted?",
    options: [
      "It is decreased.",
      "It is increased.",
      "It remains the same.",
      "It is set to zero.",
    ],
    correct: 1,
    rationale: "In a reverse split (e.g., 1-for-5), the stock price rises, so the strike price must rise proportionally to stay relevant.",
    type: "concept",
  },
  {
    id: 55,
    text: "\"Put-Call Parity\" would be violated if a trader could:",
    options: [
      "Buy a call, sell a put, and short the stock for more than the present value of the strike.",
      "Hedge a position using only calls.",
      "Calculate the Delta of an ATM option.",
      "Buy an option on the Montreal Exchange.",
    ],
    correct: 0,
    rationale: "This is the basis of a Conversion or Reversal arbitrage strategy.",
    type: "hard",
  },
  {
    id: 56,
    text: "An option has a Theta of −0.08. Over a 3-day long weekend, how much value is expected to be lost to time decay?",
    options: ["$0.08", "$0.16", "$0.24", "$0.00 (Theta only applies on trading days)."],
    correct: 2,
    rationale: "Theta is generally calculated on a calendar-day basis. −0.08 × 3 = −$0.24.",
    type: "numerical",
  },
  {
    id: 57,
    text: "\"Moneyness\" refers to:",
    options: [
      "How much cash is in the client's account.",
      "The relationship between the current stock price and the strike price.",
      "The amount of commission charged.",
      "The interest rate on a margin loan.",
    ],
    correct: 1,
    rationale: "It describes whether an option is ITM, ATM, or OTM.",
    type: "concept",
  },
  {
    id: 58,
    text: "An investor is Short a call with a Gamma of 0.12. If the stock price rises by $0.50, what is the change in the investor's Delta?",
    options: [
      "Delta increases by 0.06.",
      "Delta decreases by 0.06.",
      "Delta increases by 0.12.",
      "Delta decreases by 0.12.",
    ],
    correct: 1,
    rationale: "Short options have Negative Gamma. Change = −0.12 × 0.50 = −0.06.",
    type: "numerical",
  },
  {
    id: 59,
    text: "In the Black-Scholes model, the \"Standard Deviation\" of returns is a proxy for:",
    options: ["Interest rates.", "Volatility.", "Time decay.", "Dividend yield."],
    correct: 1,
    rationale: "This is the mathematical definition of volatility used in the formula.",
    type: "concept",
  },
  {
    id: 60,
    text: "Which of the following markets is the \"Underlying\" for the SXO Index Option?",
    options: [
      "The S&P 500.",
      "The S&P/TSX 60 Index.",
      "The Toronto Stock Exchange total volume.",
      "Government of Canada 10-year bonds.",
    ],
    correct: 1,
    rationale: "The SXO tracks the top 60 companies on the TSX.",
    type: "concept",
  },
  {
    id: 61,
    text: "Which of the following describes the \"Volatility Smile\"?",
    options: [
      "A pattern where ATM options have higher implied volatility than OTM options.",
      "A pattern where implied volatility is higher for both deep OTM and deep ITM options compared to ATM options.",
      "A graph showing that volatility always decreases as time to expiration increases.",
      "The relationship between the stock price and the interest rate.",
    ],
    correct: 1,
    rationale: "The smile reflects the market's tendency to price in a higher probability of extreme moves (tail risk) than a standard normal distribution would suggest.",
    type: "concept",
  },
  {
    id: 62,
    text: "A 3-month futures contract on a stock is priced at $101.50. The spot price is $100.00. What is the \"Basis\"?",
    options: ["+$1.50", "−$1.50", "$0.00", "6%"],
    correct: 1,
    rationale: "Basis = Spot Price − Futures Price = $100.00 − $101.50 = −$1.50. When the future is higher than the spot, the basis is negative.",
    type: "numerical",
  },
  {
    id: 63,
    text: "A portfolio manager is Delta-Hedging a long position in call options by shorting the underlying stock. As the stock price rises, the manager must:",
    options: [
      "Sell more stock to remain delta-neutral.",
      "Buy back some of the shorted stock.",
      "Do nothing, as the hedge is already in place.",
      "Close the option position immediately.",
    ],
    correct: 0,
    rationale: "As the stock rises, the Delta of the calls increases (closer to 1.00). To offset this increased bullishness, the manager must increase the size of the short stock position.",
    type: "case",
  },
  {
    id: 64,
    text: "In the \"Cost of Carry\" model for a commodity like Gold, which factor is usually considered to be zero?",
    options: ["Interest rates.", "Storage costs.", "Dividend yield.", "Insurance costs."],
    correct: 2,
    rationale: "Gold does not pay dividends or interest; it only incurs costs (storage/insurance) and interest on the capital used to hold it.",
    type: "concept",
  },
  {
    id: 65,
    text: "\"Negative Gamma\" is often referred to as being:",
    options: [
      "\"Long the wings.\"",
      "\"Short the market.\"",
      "\"Short volatility.\"",
      "\"Long time.\"",
    ],
    correct: 2,
    rationale: "Negative Gamma positions (like short straddles) lose money if the underlying asset moves significantly (high volatility).",
    type: "concept",
  },
  {
    id: 66,
    text: "An option has a Rho of −0.05. If the risk-free rate falls by 0.50%, what is the expected change in the option's premium?",
    options: ["$0.025 increase", "$0.025 decrease", "$0.25 increase", "$0.25 decrease"],
    correct: 0,
    rationale: "Change = Rho × Change in Rate = −0.05 × −0.50 = +0.025. (Puts have negative Rho; as rates fall, put prices rise.)",
    type: "numerical",
  },
  {
    id: 67,
    text: "\"Arbitrage-Free Pricing\" implies that:",
    options: [
      "The price of a derivative is equal to the cost of replicating its payoff using the underlying asset and cash.",
      "Options are always overpriced.",
      "Markets are always in backwardation.",
      "Transaction costs are the most important part of the formula.",
    ],
    correct: 0,
    rationale: "This is the foundational principle of all modern derivative pricing models.",
    type: "concept",
  },
  {
    id: 68,
    text: "Which of the following Greeks is most sensitive to a \"Volatility Crush\" (a sudden drop in IV) following an earnings announcement?",
    options: ["Delta", "Gamma", "Theta", "Vega"],
    correct: 3,
    rationale: "Vega measures the dollar impact of a 1% change in IV. A massive drop in IV will cause the premium to collapse, even if the stock price doesn't move.",
    type: "hard",
  },
  {
    id: 69,
    text: "A stock is trading at $80. A $75 Call is trading at $7.50. What is the \"Time Value\" of this option?",
    options: ["$5.00", "$7.50", "$2.50", "$0.00"],
    correct: 2,
    rationale: "Intrinsic Value = $80 − $75 = $5.00. Time Value = Total Premium − Intrinsic Value = $7.50 − $5.00 = $2.50.",
    type: "numerical",
  },
  {
    id: 70,
    text: "\"Continuous Compounding\" is often used in the Black-Scholes model because:",
    options: [
      "It is easier for retail investors to calculate.",
      "It accurately reflects how financial markets operate in real-time.",
      "It makes the Delta of an option always equal to 1.00.",
      "It eliminates the need for the risk-free rate.",
    ],
    correct: 1,
    rationale: "Theoretical models assume interest and returns accrue continuously rather than in discrete steps.",
    type: "concept",
  },
  {
    id: 71,
    text: "If an option is \"Deep Out-of-the-Money,\" its Gamma will be:",
    options: ["Very high.", "Near zero.", "Negative.", "Exactly 1.00."],
    correct: 1,
    rationale: "Gamma peaks for at-the-money options. For deep OTM or ITM options, the Delta doesn't change much when the stock moves, so Gamma is low.",
    type: "concept",
  },
  {
    id: 72,
    text: "A trader is \"Short Theta.\" This means they:",
    options: [
      "Benefit from the passage of time.",
      "Lose money with the passage of time.",
      "Are hedging a futures position.",
      "Are expecting a dividend.",
    ],
    correct: 1,
    rationale: "Option buyers are Short Theta (time is their enemy); option sellers are Long Theta (time is their friend).",
    type: "case",
  },
  {
    id: 73,
    text: "The \"Risk-Free Rate\" used in Canadian derivative pricing is typically the yield on:",
    options: [
      "The S&P/TSX 60 Index.",
      "3-month Government of Canada Treasury Bills.",
      "The prime lending rate of a major bank.",
      "Corporate bonds.",
    ],
    correct: 1,
    rationale: "T-Bills are considered the standard benchmark for risk-free liquid capital in Canada.",
    type: "concept",
  },
  {
    id: 74,
    text: "An option has a Vega of 0.10. If the current premium is $4.00 and Implied Volatility drops by 3%, what is the new premium?",
    options: ["$4.30", "$4.10", "$3.90", "$3.70"],
    correct: 3,
    rationale: "Change = 0.10 × −3 = −$0.30. New Premium = $4.00 − $0.30 = $3.70.",
    type: "numerical",
  },
  {
    id: 75,
    text: "\"Dividends\" reduce the price of the underlying stock on which day?",
    options: [
      "The declaration date.",
      "The record date.",
      "The ex-dividend date.",
      "The payment date.",
    ],
    correct: 2,
    rationale: "The stock price is adjusted downward on the ex-dividend date, which is when the buyer is no longer entitled to the dividend.",
    type: "concept",
  },
  {
    id: 76,
    text: "\"Vanna\" is a second-order Greek that measures the sensitivity of:",
    options: [
      "Delta to a change in Volatility.",
      "Gamma to a change in Time.",
      "Vega to a change in Interest Rates.",
      "Rho to a change in the Stock Price.",
    ],
    correct: 0,
    rationale: "Vanna is an advanced concept important for understanding how a hedge's effectiveness changes as volatility shifts.",
    type: "hard",
  },
  {
    id: 77,
    text: "\"Put-Call Parity\" would hold that a synthetic long stock position is created by:",
    options: [
      "Buying a call and buying a put.",
      "Selling a call and selling a put.",
      "Buying a call and selling a put (at the same strike/expiry).",
      "Selling a call and buying a put.",
    ],
    correct: 2,
    rationale: "Long Call + Short Put = Synthetic Long Stock.",
    type: "concept",
  },
  {
    id: 78,
    text: "If a stock at $50 pays a $1.00 dividend in 2 months, what is the impact on the theoretical price of a 3-month $50 Call?",
    options: [
      "It will be roughly $1.00 higher than a non-dividend-paying version.",
      "It will be roughly $1.00 lower than a non-dividend-paying version.",
      "It will remain unchanged.",
      "It will double in value.",
    ],
    correct: 1,
    rationale: "Since the stock price will drop by $1.00 on the ex-date, the call (the right to buy) becomes less valuable.",
    type: "numerical",
  },
  {
    id: 79,
    text: "\"Maturity\" in a futures contract is the date on which:",
    options: [
      "The investor must open the position.",
      "The daily settlement stops and final delivery or cash settlement occurs.",
      "The RR's commission is doubled.",
      "The margin requirement is waived.",
    ],
    correct: 1,
    rationale: "This is the end of life for the specific contract series.",
    type: "concept",
  },
  {
    id: 80,
    text: "Which of the following is NOT an assumption of the Black-Scholes model?",
    options: [
      "Markets are efficient.",
      "No transaction costs or taxes.",
      "Volatility is constant over the life of the option.",
      "The stock price moves in discrete $1.00 increments.",
    ],
    correct: 3,
    rationale: "Black-Scholes assumes stock prices move in a continuous random walk (log-normal distribution).",
    type: "hard",
  },
  {
    id: 81,
    text: "An option has a Delta of 0.40. If the stock moves from $50 to $52, by how much should the option premium increase?",
    options: ["$0.40", "$0.80", "$2.00", "$1.20"],
    correct: 1,
    rationale: "Change = Delta × Change in Stock = 0.40 × $2.00 = $0.80.",
    type: "numerical",
  },
  {
    id: 82,
    text: "\"Forward Price\" is a single price agreed upon today for delivery in the future, whereas \"Futures Price\":",
    options: [
      "Is always lower.",
      "Changes daily as the contract is marked-to-market.",
      "Is only for agricultural products.",
      "Is determined by the government.",
    ],
    correct: 1,
    rationale: "Futures are exchange-traded and re-priced every session.",
    type: "concept",
  },
  {
    id: 83,
    text: "An investor notices that Theta is higher for an option expiring in 1 week compared to one expiring in 6 months. This is because:",
    options: [
      "Time decay is constant.",
      "Time decay accelerates as the option nears expiration.",
      "1-week options have higher interest rate risk.",
      "The multiplier changes over time.",
    ],
    correct: 1,
    rationale: "Theta is not linear; it burns faster as the clock runs out.",
    type: "case",
  },
  {
    id: 84,
    text: "\"Intrinsic Value\" of a Put option is calculated as:",
    options: [
      "Stock Price − Strike Price.",
      "Strike Price − Stock Price (or zero, whichever is higher).",
      "Premium − Vega.",
      "Strike Price + Premium.",
    ],
    correct: 1,
    rationale: "A put is only intrinsically valuable if the strike (sale price) is higher than the current market price.",
    type: "concept",
  },
  {
    id: 85,
    text: "A 1-year European Call is $8 and a 1-year European Put is $5 (both $100 strike), and the stock is $101. What is the implied risk-free rate using simple interest? (C − P = S − K/(1+r))",
    options: ["1%", "2%", "3%", "4%"],
    correct: 1,
    rationale: "8 − 5 = 101 − 100/(1+r) → 3 = 101 − 100/(1+r) → 100/(1+r) = 98 → 1+r = 100/98 ≈ 1.02 → r = 2%.",
    type: "numerical",
  },
  {
    id: 86,
    text: "\"Vega\" is always a ______ number for a Long Option position.",
    options: ["Positive", "Negative", "Zero", "Neutral"],
    correct: 0,
    rationale: "If you own an option, you want volatility to increase because it increases the premium.",
    type: "concept",
  },
  {
    id: 87,
    text: "Which market provides the \"Liquidity\" for index futures?",
    options: [
      "The physical retail market.",
      "Market makers and high-frequency traders.",
      "Only the Bank of Canada.",
      "The insurance industry.",
    ],
    correct: 1,
    rationale: "Professional liquidity providers ensure there is always a bid and ask price available.",
    type: "concept",
  },
  {
    id: 88,
    text: "\"Gamma Scalping\" is a strategy where a trader:",
    options: [
      "Sells as many options as possible to collect theta.",
      "Adjusts a delta-neutral hedge as the stock moves to lock in small profits.",
      "Only trades options with 1 day to expiration.",
      "Avoids all volatility risk.",
    ],
    correct: 1,
    rationale: "Long Gamma traders buy when the stock dips and sell when it rises to keep their hedge neutral, profiting from the curvature of the option.",
    type: "concept",
  },
  {
    id: 89,
    text: "If an investor is \"Short Gamma\" and the market becomes very volatile, their potential for loss is:",
    options: [
      "Limited to the premium they paid.",
      "Limited to the margin in their account.",
      "Accelerating and theoretically large.",
      "Non-existent.",
    ],
    correct: 2,
    rationale: "Negative Gamma means your Delta moves against you at an increasing rate (e.g., you get shorter as the stock rises).",
    type: "hard",
  },
  {
    id: 90,
    text: "The \"Underlying Price\" is the primary driver of which Greek(s)?",
    options: ["Delta", "Gamma", "Both Delta and Gamma", "Vega"],
    correct: 2,
    rationale: "Delta is the first derivative (price) and Gamma is the second derivative (price of the price).",
    type: "concept",
  },
  {
    id: 91,
    text: "Which of the following describes the impact of an \"Interest Rate Hike\" on a futures contract for a non-dividend-paying stock?",
    options: [
      "The futures price will decrease.",
      "The futures price will increase.",
      "The spot price will automatically rise to match the future.",
      "There is no impact on futures pricing.",
    ],
    correct: 1,
    rationale: "According to the Cost of Carry model (F = S × [1 + r × t]), higher interest rates increase the cost of holding the underlying asset, driving the theoretical futures price higher.",
    type: "concept",
  },
  {
    id: 92,
    text: "An option has a Delta of 0.50 and a Vega of 0.25. If the stock price rises by $2.00 and Implied Volatility drops by 2% simultaneously, what is the net estimated change in the option premium?",
    options: ["+$1.00", "+$0.50", "+$1.50", "−$0.50"],
    correct: 1,
    rationale: "Delta impact = 0.50 × $2.00 = +$1.00. Vega impact = 0.25 × (−2) = −$0.50. Net change = $1.00 − $0.50 = +$0.50.",
    type: "numerical",
  },
  {
    id: 93,
    text: "If a trader is \"Short Gamma\" and \"Short Vega,\" which market environment would be most profitable for them?",
    options: [
      "A fast-moving bull market with rising volatility.",
      "A stagnant market with decreasing implied volatility.",
      "A market crash with a massive volatility spike.",
      "A high-interest-rate environment with low volume.",
    ],
    correct: 1,
    rationale: "Being short Gamma/Vega means the trader profits when the stock stays still (neutral) and when fear (volatility) leaves the market.",
    type: "hard",
  },
  {
    id: 94,
    text: "A European Call is trading for $5.00 and a European Put is trading for $3.00 (both $50 strike). The stock is at $52. The risk-free rate is zero. Is there an arbitrage opportunity?",
    options: [
      "No, the prices are perfectly aligned.",
      "Yes, the synthetic stock (C − P) is worth $2.00, but the actual stock is $52; the Call is too cheap.",
      "Yes, the trader should sell the Call and buy the Put.",
      "No, because the Delta is not 1.00.",
    ],
    correct: 0,
    rationale: "Under Put-Call Parity with r=0, C − P = S − K. Here, $5 − $3 = $2 and S − K = $52 − $50 = $2. Both sides equal $2, so the prices are aligned — no arbitrage exists.",
    type: "case",
  },
  {
    id: 95,
    text: "\"Time Decay\" (Theta) is a \"Zero-Sum\" feature in a trade. This means:",
    options: [
      "The money the buyer loses to time decay is exactly the amount the seller gains.",
      "Time decay stops when the stock price reaches the strike.",
      "Theta is always zero for at-the-money options.",
      "The Montreal Exchange keeps the time value.",
    ],
    correct: 0,
    rationale: "Derivatives are a transfer of risk and value; the bleeding of time value from the long position is the income for the short position.",
    type: "concept",
  },
  {
    id: 96,
    text: "Which pricing model is specifically based on the \"Log-Normal\" distribution of stock prices?",
    options: [
      "Binomial Model.",
      "Black-Scholes Model.",
      "Cost of Carry Model.",
      "Linear Regression.",
    ],
    correct: 1,
    rationale: "Black-Scholes assumes stock prices cannot go below zero and follow a continuous random walk.",
    type: "concept",
  },
  {
    id: 97,
    text: "An option has a Rho of 0.08. A trader holds 100 contracts. If interest rates rise by 0.25%, what is the total dollar impact on the position?",
    options: ["$2.00", "$20.00", "$200.00", "$2,000.00"],
    correct: 2,
    rationale: "Change per share = 0.08 × 0.25 = $0.02. Total impact = $0.02 × 100 shares × 100 contracts = $200.",
    type: "numerical",
  },
  {
    id: 98,
    text: "\"Skewness\" in the volatility surface usually indicates that the market is:",
    options: [
      "Assigning a higher probability to a market crash than a market rally.",
      "Expecting interest rates to stay flat.",
      "Trading only in European-style options.",
      "Completely efficient.",
    ],
    correct: 0,
    rationale: "Fear of the downside typically makes Out-of-the-Money Puts trade at higher Implied Volatilities than OTM Calls.",
    type: "concept",
  },
  {
    id: 99,
    text: "If an investor is \"Delta-Neutral\" but \"Long Gamma,\" a large move in the stock (up or down) will result in:",
    options: [
      "A loss.",
      "A profit.",
      "No change in value.",
      "An immediate margin call.",
    ],
    correct: 1,
    rationale: "Long Gamma means your Delta works for you; as the stock moves, it makes your position more bullish (if rising) or more bearish (if falling), creating a profit smile.",
    type: "hard",
  },
  {
    id: 100,
    text: "The \"Forward Price\" of a commodity includes the \"Spot Price\" plus:",
    options: [
      "The RR's commission.",
      "The Cost of Carry (Interest + Storage + Insurance).",
      "The Delta of the nearest call option.",
      "The historical volatility of the last 10 years.",
    ],
    correct: 1,
    rationale: "The forward/futures price is derived from the spot price plus all costs of carrying the physical commodity.",
    type: "concept",
  },
];

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TYPE_LABELS = {
  concept: "Conceptual",
  numerical: "Numerical",
  case: "Case Study",
  hard: "Hard",
};

const TYPE_COLORS = {
  concept: { bg: "#dbeafe", text: "#1e40af" },
  numerical: { bg: "#dcfce7", text: "#166534" },
  case: { bg: "#fef3c7", text: "#92400e" },
  hard: { bg: "#fee2e2", text: "#991b1b" },
};

const QUIZ_DURATION = 75 * 60; // 75 minutes in seconds

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt2 = (n) => String(n).padStart(2, "0");
const fmtTime = (secs) => `${fmt2(Math.floor(secs / 60))}:${fmt2(secs % 60)}`;

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────
const VittaLogo = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <div style={{
      width: 36, height: 36, borderRadius: "50%",
      background: "linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 2px 8px rgba(30,58,95,0.25)",
    }}>
      <span style={{ color: "#d4a017", fontFamily: "Georgia, serif", fontWeight: "bold", fontSize: 15 }}>V</span>
    </div>
    <div>
      <div style={{ fontFamily: "Georgia, serif", fontWeight: "bold", fontSize: 13, color: "#1e3a5f", letterSpacing: 1.5, textTransform: "uppercase" }}>Vitta Finance Academy</div>
      <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 0.5 }}>CIRO Derivatives Exam Prep</div>
    </div>
  </div>
);

const TypeBadge = ({ type }) => {
  const c = TYPE_COLORS[type] || TYPE_COLORS.concept;
  return (
    <span style={{
      background: c.bg, color: c.text,
      padding: "3px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
    }}>
      {TYPE_LABELS[type] || type}
    </span>
  );
};

const ProgressBar = ({ current, total, answered }) => (
  <div style={{ padding: "10px 24px 0", background: "#fff" }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
      <span style={{ fontSize: 11, color: "#64748b" }}>Q {current} of {total}</span>
      <span style={{ fontSize: 11, color: "#64748b" }}>{answered} answered</span>
    </div>
    <div style={{ height: 4, background: "#e2e8f0", borderRadius: 2, overflow: "hidden" }}>
      <div style={{
        height: "100%", borderRadius: 2,
        background: "linear-gradient(90deg, #1e3a5f, #2d5986)",
        width: `${(answered / total) * 100}%`,
        transition: "width 0.3s ease",
      }} />
    </div>
  </div>
);

// ─── HOME SCREEN ─────────────────────────────────────────────────────────────
const HomeScreen = ({ onMode }) => (
  <div style={{
    minHeight: "100vh", background: "#f8fafc",
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: 24,
  }}>
    {/* Header card */}
    <div style={{
      background: "#fff", borderRadius: 20,
      boxShadow: "0 4px 32px rgba(30,58,95,0.10)",
      padding: "48px 40px", maxWidth: 520, width: "100%",
      textAlign: "center",
      border: "1px solid #e2e8f0",
    }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 16px",
          boxShadow: "0 6px 20px rgba(30,58,95,0.25)",
        }}>
          <span style={{ color: "#d4a017", fontFamily: "Georgia, serif", fontWeight: "bold", fontSize: 30 }}>V</span>
        </div>
        <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#d4a017", fontWeight: 600, marginBottom: 6 }}>
          Vitta Finance Academy
        </div>
        <h1 style={{
          fontFamily: "Georgia, serif", fontSize: 26, fontWeight: "bold",
          color: "#0f172a", margin: 0, lineHeight: 1.3,
        }}>
          CIRO Derivatives Exam
        </h1>
        <div style={{
          display: "inline-block", marginTop: 10, padding: "5px 18px",
          background: "#1e3a5f", color: "#fff",
          borderRadius: 20, fontSize: 13, fontWeight: 600,
          letterSpacing: 0.5,
        }}>
          Element 4: Pricing & Underlying Markets
        </div>
      </div>

      <p style={{ color: "#475569", fontSize: 14, lineHeight: 1.7, margin: "20px 0 28px" }}>
        100 questions covering Black-Scholes, Greeks, Put-Call Parity, Cost of Carry, volatility, and pricing models. Choose your mode below.
      </p>

      {/* Stats row */}
      <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        {[
          { label: "Questions", value: "100", icon: "📋" },
          { label: "Topics", value: "6", icon: "📚" },
          { label: "Quiz Time", value: "75 min", icon: "⏱" },
        ].map(({ label, value, icon }) => (
          <div key={label} style={{
            background: "#f1f5f9", borderRadius: 12, padding: "12px 18px",
            textAlign: "center", minWidth: 90,
          }}>
            <div style={{ fontSize: 18 }}>{icon}</div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: "bold", color: "#1e3a5f" }}>{value}</div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Mode buttons */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <button onClick={() => onMode("practice")} style={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)",
          color: "#fff", border: "none", borderRadius: 12, padding: "16px 24px",
          fontSize: 15, fontWeight: 700, cursor: "pointer",
          boxShadow: "0 4px 14px rgba(30,58,95,0.30)",
          fontFamily: "Georgia, serif", letterSpacing: 0.3,
          transition: "opacity 0.2s",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        }}>
          <span>📖</span> Practice Mode
        </button>
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: -4, marginBottom: 4 }}>
          Instant feedback · Rationale explanations · Missed question review
        </div>
        <button onClick={() => onMode("quiz")} style={{
          background: "#fff", color: "#1e3a5f",
          border: "2px solid #1e3a5f", borderRadius: 12, padding: "16px 24px",
          fontSize: 15, fontWeight: 700, cursor: "pointer",
          fontFamily: "Georgia, serif", letterSpacing: 0.3,
          transition: "all 0.2s",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        }}>
          <span>⏱</span> Timed Quiz Mode
        </button>
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: -4 }}>
          75-minute exam simulation · Full diagnostics & score breakdown
        </div>
      </div>
    </div>

    {/* Legend */}
    <div style={{ marginTop: 20, display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
      {Object.entries(TYPE_LABELS).map(([k, v]) => (
        <TypeBadge key={k} type={k} />
      ))}
    </div>
  </div>
);

// ─── QUESTION CARD ────────────────────────────────────────────────────────────
const QuestionCard = ({
  question, selectedAnswer, onSelect, showResult, mode,
}) => {
  const letters = ["A", "B", "C", "D"];

  return (
    <div style={{ padding: "20px 24px 0" }}>
      {/* Q header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{
          background: "#1e3a5f", color: "#d4a017",
          fontFamily: "Georgia, serif", fontWeight: "bold", fontSize: 13,
          borderRadius: 8, padding: "4px 12px", letterSpacing: 0.5,
        }}>
          Q{question.id}
        </div>
        <TypeBadge type={question.type} />
      </div>

      {/* Question text */}
      <p style={{
        fontFamily: "Georgia, serif", fontSize: 15.5, color: "#0f172a",
        lineHeight: 1.7, margin: "0 0 18px", fontWeight: "normal",
      }}>
        {question.text}
      </p>

      {/* Options */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {question.options.map((opt, i) => {
          const isSelected = selectedAnswer === i;
          const isCorrect = i === question.correct;
          let bg = "#f8fafc";
          let border = "1px solid #e2e8f0";
          let textColor = "#1e293b";
          let weight = "normal";

          if (showResult) {
            if (isCorrect) {
              bg = "#dcfce7"; border = "1.5px solid #166534"; textColor = "#166534"; weight = "600";
            } else if (isSelected && !isCorrect) {
              bg = "#fee2e2"; border = "1.5px solid #991b1b"; textColor = "#991b1b"; weight = "600";
            }
          } else if (isSelected) {
            bg = "#dbeafe"; border = "1.5px solid #1e40af"; textColor = "#1e40af"; weight = "600";
          }

          return (
            <button
              key={i}
              onClick={() => !showResult && onSelect(i)}
              style={{
                background: bg, border, borderRadius: 10,
                padding: "12px 16px", textAlign: "left",
                cursor: showResult ? "default" : "pointer",
                transition: "all 0.15s", width: "100%",
                display: "flex", alignItems: "flex-start", gap: 10,
              }}
            >
              <span style={{
                minWidth: 24, height: 24, borderRadius: 6,
                background: showResult
                  ? isCorrect ? "#166534" : isSelected ? "#991b1b" : "#e2e8f0"
                  : isSelected ? "#1e40af" : "#e2e8f0",
                color: (isSelected || (showResult && isCorrect)) ? "#fff" : "#64748b",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: "bold", fontSize: 12, flexShrink: 0,
              }}>
                {showResult && isCorrect ? "✓" : showResult && isSelected && !isCorrect ? "✗" : letters[i]}
              </span>
              <span style={{ fontSize: 14, color: textColor, fontWeight: weight, lineHeight: 1.5 }}>
                {opt}
              </span>
            </button>
          );
        })}
      </div>

      {/* Rationale */}
      {showResult && (
        <div style={{
          marginTop: 16, padding: "14px 16px",
          background: "#f0fdf4", borderRadius: 10,
          borderLeft: "4px solid #166534",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#166534", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Rationale
          </div>
          <p style={{ margin: 0, fontSize: 13.5, color: "#166534", lineHeight: 1.6 }}>
            {question.rationale}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── DOT NAVIGATOR ────────────────────────────────────────────────────────────
const DotNavigator = ({ total, current, answers, onGo }) => {
  return (
    <div style={{
      padding: "14px 20px",
      overflowX: "auto",
      borderTop: "1px solid #e2e8f0",
    }}>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "center" }}>
        {Array.from({ length: total }, (_, i) => {
          const answered = answers[i] !== undefined;
          const isCurrent = i === current;
          return (
            <button
              key={i}
              onClick={() => onGo(i)}
              style={{
                width: isCurrent ? 28 : 22, height: isCurrent ? 28 : 22,
                borderRadius: 6,
                background: isCurrent ? "#1e3a5f" : answered ? "#d4a017" : "#e2e8f0",
                color: (isCurrent || answered) ? "#fff" : "#94a3b8",
                border: "none", cursor: "pointer",
                fontSize: 9, fontWeight: "bold",
                transition: "all 0.15s", flexShrink: 0,
              }}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 8 }}>
        <span style={{ fontSize: 10, color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 10, height: 10, background: "#1e3a5f", borderRadius: 3, display: "inline-block" }} /> Current
        </span>
        <span style={{ fontSize: 10, color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 10, height: 10, background: "#d4a017", borderRadius: 3, display: "inline-block" }} /> Answered
        </span>
        <span style={{ fontSize: 10, color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 10, height: 10, background: "#e2e8f0", borderRadius: 3, display: "inline-block" }} /> Unanswered
        </span>
      </div>
    </div>
  );
};

// ─── PRACTICE MODE ────────────────────────────────────────────────────────────
const PracticeMode = ({ onHome }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [subMode, setSubMode] = useState("practice"); // practice | missed | results

  const q = QUESTIONS[current];
  const totalAnswered = Object.keys(answers).length;
  const isRevealed = !!revealed[current];
  const isAnswered = answers[current] !== undefined;

  const handleSelect = (i) => {
    if (isRevealed) return;
    setAnswers((prev) => ({ ...prev, [current]: i }));
    setRevealed((prev) => ({ ...prev, [current]: true }));
  };

  const next = () => { if (current < QUESTIONS.length - 1) setCurrent(current + 1); };
  const prev = () => { if (current > 0) setCurrent(current - 1); };

  const score = Object.entries(answers).filter(([idx, ans]) => ans === QUESTIONS[parseInt(idx)].correct).length;
  const missedIdx = Object.entries(answers)
    .filter(([idx, ans]) => ans !== QUESTIONS[parseInt(idx)].correct)
    .map(([idx]) => parseInt(idx));

  if (subMode === "results") {
    return (
      <PracticeResults
        score={score}
        total={totalAnswered}
        missed={missedIdx}
        onHome={onHome}
        onReview={() => { setSubMode("missed"); setCurrent(missedIdx[0] || 0); }}
        onRestart={() => { setAnswers({}); setRevealed({}); setCurrent(0); setSubMode("practice"); }}
      />
    );
  }

  const displayQuestions = subMode === "missed"
    ? missedIdx.map(i => QUESTIONS[i])
    : QUESTIONS;

  const displayQ = displayQuestions[current] || QUESTIONS[0];

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{
        background: "#fff", borderBottom: "1px solid #e2e8f0",
        padding: "14px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 10,
        position: "sticky", top: 0, zIndex: 10,
      }}>
        <VittaLogo />
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{
            background: "#dbeafe", color: "#1e40af",
            padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
          }}>
            📖 Practice Mode
          </span>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {score}/{totalAnswered} correct
          </span>
          {totalAnswered > 0 && (
            <button
              onClick={() => setSubMode("results")}
              style={{
                background: "#1e3a5f", color: "#d4a017", border: "none",
                borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700,
                cursor: "pointer",
              }}
            >
              See Results
            </button>
          )}
          <button onClick={onHome} style={{
            background: "none", border: "1px solid #e2e8f0", color: "#64748b",
            borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer",
          }}>
            ← Home
          </button>
        </div>
      </div>

      <ProgressBar current={current + 1} total={QUESTIONS.length} answered={totalAnswered} />

      {/* Question area */}
      <div style={{ flex: 1, maxWidth: 760, margin: "0 auto", width: "100%", paddingBottom: 20 }}>
        <div style={{
          background: "#fff", margin: "16px 16px 0", borderRadius: 14,
          boxShadow: "0 2px 12px rgba(30,58,95,0.08)", border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}>
          <QuestionCard
            question={displayQ}
            selectedAnswer={answers[subMode === "missed" ? missedIdx[current] : current]}
            onSelect={handleSelect}
            showResult={subMode === "missed" ? true : isRevealed}
          />

          {/* Nav buttons */}
          <div style={{
            display: "flex", justifyContent: "space-between", padding: "16px 24px 20px",
            borderTop: "1px solid #f1f5f9", marginTop: 16,
          }}>
            <button onClick={prev} disabled={current === 0} style={{
              background: current === 0 ? "#f1f5f9" : "#f8fafc",
              color: current === 0 ? "#94a3b8" : "#1e3a5f",
              border: "1px solid #e2e8f0", borderRadius: 8,
              padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: current === 0 ? "default" : "pointer",
            }}>
              ← Prev
            </button>

            <div style={{ display: "flex", gap: 8 }}>
              {!isRevealed && !isAnswered && (
                <button
                  onClick={() => setRevealed(prev => ({ ...prev, [current]: true }))}
                  style={{
                    background: "#fef3c7", color: "#92400e",
                    border: "1px solid #fde68a", borderRadius: 8,
                    padding: "10px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Show Answer
                </button>
              )}
              <button
                onClick={next}
                disabled={current === QUESTIONS.length - 1}
                style={{
                  background: current === QUESTIONS.length - 1 ? "#f1f5f9" : "linear-gradient(135deg, #1e3a5f, #2d5986)",
                  color: current === QUESTIONS.length - 1 ? "#94a3b8" : "#fff",
                  border: "none", borderRadius: 8,
                  padding: "10px 20px", fontSize: 13, fontWeight: 600,
                  cursor: current === QUESTIONS.length - 1 ? "default" : "pointer",
                  boxShadow: current === QUESTIONS.length - 1 ? "none" : "0 2px 8px rgba(30,58,95,0.2)",
                }}
              >
                Next →
              </button>
            </div>
          </div>
        </div>

        {/* Navigator */}
        <div style={{
          background: "#fff", margin: "12px 16px 0", borderRadius: 14,
          boxShadow: "0 2px 12px rgba(30,58,95,0.06)", border: "1px solid #e2e8f0",
        }}>
          <DotNavigator
            total={QUESTIONS.length}
            current={current}
            answers={answers}
            onGo={setCurrent}
          />
        </div>
      </div>
    </div>
  );
};

// ─── PRACTICE RESULTS ─────────────────────────────────────────────────────────
const PracticeResults = ({ score, total, missed, onHome, onReview, onRestart }) => {
  const pct = total > 0 ? Math.round((score / total) * 100) : 0;
  const pass = pct >= 60;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "24px 16px" }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <div style={{
          background: "#fff", borderRadius: 16,
          boxShadow: "0 4px 24px rgba(30,58,95,0.10)", border: "1px solid #e2e8f0",
          padding: "32px 28px", textAlign: "center",
        }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>{pass ? "🎯" : "📚"}</div>
          <h2 style={{ fontFamily: "Georgia, serif", color: "#0f172a", margin: "0 0 4px", fontSize: 22 }}>
            Practice Results
          </h2>
          <div style={{
            fontSize: 48, fontFamily: "Georgia, serif", fontWeight: "bold",
            color: pass ? "#166534" : "#991b1b", margin: "12px 0",
          }}>
            {pct}%
          </div>
          <div style={{ fontSize: 14, color: "#64748b", marginBottom: 20 }}>
            {score} correct out of {total} attempted
          </div>

          {/* Type breakdown */}
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginBottom: 24 }}>
            {Object.keys(TYPE_LABELS).map((type) => {
              const typeQs = QUESTIONS.filter(q => q.type === type);
              const typeAnswered = typeQs.filter((_, idx) => {
                const globalIdx = QUESTIONS.findIndex(q => q === typeQs[idx]);
                return true;
              });
              return <TypeBadge key={type} type={type} />;
            })}
          </div>

          {missed.length > 0 && (
            <div style={{
              background: "#fff7ed", border: "1px solid #fed7aa",
              borderRadius: 10, padding: "12px 16px", marginBottom: 20, textAlign: "left",
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
                Missed {missed.length} question{missed.length !== 1 ? "s" : ""}:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {missed.map(i => (
                  <span key={i} style={{
                    background: "#fee2e2", color: "#991b1b",
                    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                  }}>Q{i + 1}</span>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {missed.length > 0 && (
              <button onClick={onReview} style={{
                background: "linear-gradient(135deg, #991b1b, #b91c1c)",
                color: "#fff", border: "none", borderRadius: 10,
                padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer",
              }}>
                📝 Review Missed Questions ({missed.length})
              </button>
            )}
            <button onClick={onRestart} style={{
              background: "linear-gradient(135deg, #1e3a5f, #2d5986)",
              color: "#fff", border: "none", borderRadius: 10,
              padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer",
            }}>
              🔄 Restart Practice
            </button>
            <button onClick={onHome} style={{
              background: "#f1f5f9", color: "#1e3a5f",
              border: "1px solid #e2e8f0", borderRadius: 10,
              padding: "13px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}>
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── TIMED QUIZ MODE ──────────────────────────────────────────────────────────
const TimedQuizMode = ({ onHome }) => {
  const [phase, setPhase] = useState("intro"); // intro | quiz | results
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(QUIZ_DURATION);
  const [timePerQ, setTimePerQ] = useState({});
  const [qStartTime, setQStartTime] = useState(null);
  const [flagged, setFlagged] = useState(new Set());
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (phase === "quiz") {
      startTimeRef.current = Date.now();
      setQStartTime(Date.now());
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) {
            clearInterval(timerRef.current);
            setPhase("results");
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const handleSelect = (i) => {
    const now = Date.now();
    const elapsed = qStartTime ? Math.round((now - qStartTime) / 1000) : 0;
    setTimePerQ((prev) => ({ ...prev, [current]: elapsed }));
    setQStartTime(now);
    setAnswers((prev) => ({ ...prev, [current]: i }));
  };

  const goTo = (idx) => {
    const now = Date.now();
    const elapsed = qStartTime ? Math.round((now - qStartTime) / 1000) : 0;
    setTimePerQ((prev) => ({ ...prev, [current]: (prev[current] || 0) + elapsed }));
    setQStartTime(now);
    setCurrent(idx);
  };

  const submit = () => {
    clearInterval(timerRef.current);
    setPhase("results");
  };

  const timeTaken = QUIZ_DURATION - timeLeft;
  const isWarning = timeLeft < 600; // last 10 mins
  const isCritical = timeLeft < 120; // last 2 mins

  if (phase === "intro") {
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{
          background: "#fff", borderRadius: 16, maxWidth: 480, width: "100%",
          padding: "36px 32px", boxShadow: "0 4px 24px rgba(30,58,95,0.10)",
          border: "1px solid #e2e8f0", textAlign: "center",
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⏱</div>
          <h2 style={{ fontFamily: "Georgia, serif", color: "#0f172a", margin: "0 0 12px", fontSize: 22 }}>
            Timed Quiz — Element 4
          </h2>
          <p style={{ color: "#475569", fontSize: 14, lineHeight: 1.7, margin: "0 0 24px" }}>
            100 questions · 75 minutes · Simulates real CIRO exam conditions. No feedback until submission. Full diagnostics provided at the end.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
            {[
              ["📋", "100 Questions"],
              ["⏱", "75 Minutes"],
              ["🚩", "Flag for Review"],
              ["📊", "Full Diagnostics"],
            ].map(([icon, label]) => (
              <div key={label} style={{
                background: "#f8fafc", borderRadius: 10, padding: "12px 10px",
                border: "1px solid #e2e8f0", fontSize: 13, color: "#334155",
              }}>
                <span style={{ fontSize: 18 }}>{icon}</span>
                <div style={{ marginTop: 4, fontWeight: 600 }}>{label}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setPhase("quiz")} style={{
            width: "100%", background: "linear-gradient(135deg, #1e3a5f, #2d5986)",
            color: "#fff", border: "none", borderRadius: 10,
            padding: "14px 20px", fontSize: 15, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 4px 14px rgba(30,58,95,0.25)", marginBottom: 10,
          }}>
            Start Timed Quiz →
          </button>
          <button onClick={onHome} style={{
            background: "none", border: "1px solid #e2e8f0", color: "#64748b",
            borderRadius: 10, padding: "10px 20px", fontSize: 13, cursor: "pointer", width: "100%",
          }}>
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (phase === "results") {
    return (
      <QuizResults
        answers={answers}
        timePerQ={timePerQ}
        timeTaken={timeTaken}
        onHome={onHome}
        onRestart={() => {
          setAnswers({}); setTimePerQ({}); setTimeLeft(QUIZ_DURATION);
          setCurrent(0); setFlagged(new Set()); setPhase("intro");
        }}
      />
    );
  }

  const q = QUESTIONS[current];
  const totalAnswered = Object.keys(answers).length;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{
        background: "#fff", borderBottom: "1px solid #e2e8f0",
        padding: "12px 20px", position: "sticky", top: 0, zIndex: 10,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 8,
      }}>
        <VittaLogo />
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {totalAnswered}/100 answered
          </span>
          {/* Timer */}
          <div style={{
            background: isCritical ? "#fee2e2" : isWarning ? "#fef3c7" : "#f0fdf4",
            border: `1.5px solid ${isCritical ? "#991b1b" : isWarning ? "#d97706" : "#166534"}`,
            borderRadius: 10, padding: "6px 14px",
            fontFamily: "monospace", fontSize: 18, fontWeight: "bold",
            color: isCritical ? "#991b1b" : isWarning ? "#92400e" : "#166534",
          }}>
            ⏱ {fmtTime(timeLeft)}
          </div>
          <button
            onClick={submit}
            style={{
              background: "#1e3a5f", color: "#d4a017", border: "none",
              borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}
          >
            Submit
          </button>
        </div>
      </div>

      <ProgressBar current={current + 1} total={QUESTIONS.length} answered={totalAnswered} />

      <div style={{ flex: 1, maxWidth: 760, margin: "0 auto", width: "100%", paddingBottom: 20 }}>
        <div style={{
          background: "#fff", margin: "16px 16px 0", borderRadius: 14,
          boxShadow: "0 2px 12px rgba(30,58,95,0.08)", border: "1px solid #e2e8f0",
        }}>
          <QuestionCard
            question={q}
            selectedAnswer={answers[current]}
            onSelect={handleSelect}
            showResult={false}
          />
          {/* Flag + nav */}
          <div style={{
            display: "flex", justifyContent: "space-between", padding: "16px 24px 20px",
            borderTop: "1px solid #f1f5f9", marginTop: 16,
          }}>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => current > 0 && goTo(current - 1)} disabled={current === 0} style={{
                background: current === 0 ? "#f1f5f9" : "#f8fafc",
                color: current === 0 ? "#94a3b8" : "#1e3a5f",
                border: "1px solid #e2e8f0", borderRadius: 8,
                padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: current === 0 ? "default" : "pointer",
              }}>
                ← Prev
              </button>
              <button
                onClick={() => {
                  setFlagged(prev => {
                    const next = new Set(prev);
                    if (next.has(current)) next.delete(current); else next.add(current);
                    return next;
                  });
                }}
                style={{
                  background: flagged.has(current) ? "#fef3c7" : "#f8fafc",
                  color: flagged.has(current) ? "#92400e" : "#64748b",
                  border: flagged.has(current) ? "1px solid #fde68a" : "1px solid #e2e8f0",
                  borderRadius: 8, padding: "10px 14px", fontSize: 12, cursor: "pointer",
                }}
              >
                🚩 {flagged.has(current) ? "Flagged" : "Flag"}
              </button>
            </div>
            <button onClick={() => current < QUESTIONS.length - 1 && goTo(current + 1)} disabled={current === QUESTIONS.length - 1} style={{
              background: current === QUESTIONS.length - 1 ? "#f1f5f9" : "linear-gradient(135deg, #1e3a5f, #2d5986)",
              color: current === QUESTIONS.length - 1 ? "#94a3b8" : "#fff",
              border: "none", borderRadius: 8,
              padding: "10px 20px", fontSize: 13, fontWeight: 600,
              cursor: current === QUESTIONS.length - 1 ? "default" : "pointer",
              boxShadow: current === QUESTIONS.length - 1 ? "none" : "0 2px 8px rgba(30,58,95,0.2)",
            }}>
              Next →
            </button>
          </div>
        </div>

        {/* Navigator */}
        <div style={{
          background: "#fff", margin: "12px 16px 0", borderRadius: 14,
          boxShadow: "0 2px 12px rgba(30,58,95,0.06)", border: "1px solid #e2e8f0",
        }}>
          <DotNavigator
            total={QUESTIONS.length}
            current={current}
            answers={answers}
            onGo={goTo}
          />
        </div>
      </div>
    </div>
  );
};

// ─── QUIZ RESULTS & DIAGNOSTICS ──────────────────────────────────────────────
const QuizResults = ({ answers, timePerQ, timeTaken, onHome, onRestart }) => {
  const [tab, setTab] = useState("overview"); // overview | breakdown | review
  const [reviewIdx, setReviewIdx] = useState(0);

  const total = QUESTIONS.length;
  const attempted = Object.keys(answers).length;
  const correct = Object.entries(answers).filter(([i, a]) => a === QUESTIONS[parseInt(i)].correct).length;
  const score = Math.round((correct / total) * 100);
  const pass = score >= 60;

  const missedQs = QUESTIONS.filter((q, i) => answers[i] !== undefined && answers[i] !== q.correct);
  const unanswered = QUESTIONS.filter((_, i) => answers[i] === undefined);
  const avgTime = attempted > 0
    ? Math.round(Object.values(timePerQ).reduce((a, b) => a + b, 0) / attempted)
    : 0;

  // By type diagnostics
  const byType = {};
  Object.keys(TYPE_LABELS).forEach(type => {
    const qs = QUESTIONS.filter(q => q.type === type);
    const ans = qs.filter((q, _) => {
      const gi = QUESTIONS.indexOf(q);
      return answers[gi] !== undefined;
    });
    const cor = qs.filter((q) => {
      const gi = QUESTIONS.indexOf(q);
      return answers[gi] === q.correct;
    });
    byType[type] = { total: qs.length, answered: ans.length, correct: cor.length };
  });

  const TabBtn = ({ id, label }) => (
    <button
      onClick={() => setTab(id)}
      style={{
        background: tab === id ? "#1e3a5f" : "transparent",
        color: tab === id ? "#fff" : "#64748b",
        border: tab === id ? "none" : "1px solid #e2e8f0",
        borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "20px 16px" }}>
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        {/* Score hero */}
        <div style={{
          background: pass
            ? "linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)"
            : "linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)",
          borderRadius: 16, padding: "28px 24px", textAlign: "center",
          marginBottom: 16, color: "#fff",
          boxShadow: "0 6px 24px rgba(30,58,95,0.20)",
        }}>
          <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>
            Vitta Finance Academy · Element 4
          </div>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 52, fontWeight: "bold", color: "#d4a017", lineHeight: 1 }}>
            {score}%
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, marginTop: 6 }}>
            {pass ? "✓ Pass" : "✗ Below Passing"}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", marginTop: 4 }}>
            {correct} / {total} correct · {fmtTime(timeTaken)} taken
          </div>
        </div>

        {/* Stat row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          {[
            { label: "Correct", value: correct, color: "#166534", bg: "#dcfce7" },
            { label: "Incorrect", value: attempted - correct, color: "#991b1b", bg: "#fee2e2" },
            { label: "Unanswered", value: total - attempted, color: "#92400e", bg: "#fef3c7" },
            { label: "Avg/Q", value: `${avgTime}s`, color: "#1e40af", bg: "#dbeafe" },
          ].map(({ label, value, color, bg }) => (
            <div key={label} style={{
              background: "#fff", borderRadius: 12, padding: "14px 10px",
              textAlign: "center", border: "1px solid #e2e8f0",
              boxShadow: "0 1px 6px rgba(30,58,95,0.06)",
            }}>
              <div style={{ fontFamily: "Georgia, serif", fontSize: 22, fontWeight: "bold", color }}>{value}</div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          <TabBtn id="overview" label="📊 Overview" />
          <TabBtn id="breakdown" label="🔍 By Type" />
          <TabBtn id="review" label={`📝 Review (${missedQs.length})`} />
        </div>

        {/* Tab content */}
        <div style={{
          background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
          padding: "20px", boxShadow: "0 2px 12px rgba(30,58,95,0.06)",
        }}>
          {tab === "overview" && (
            <div>
              <h3 style={{ fontFamily: "Georgia, serif", color: "#0f172a", margin: "0 0 16px", fontSize: 16 }}>
                Performance Overview
              </h3>
              {/* Score bar */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: "#64748b" }}>Score</span>
                  <span style={{ color: "#0f172a", fontWeight: 700 }}>{score}%</span>
                </div>
                <div style={{ height: 12, background: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 6,
                    background: pass
                      ? "linear-gradient(90deg, #166534, #22c55e)"
                      : "linear-gradient(90deg, #991b1b, #ef4444)",
                    width: `${score}%`, transition: "width 1s ease",
                  }} />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>Passing: 60%</span>
                </div>
              </div>

              {/* Completion */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: "#64748b" }}>Questions Attempted</span>
                  <span style={{ fontWeight: 700 }}>{attempted}/100</span>
                </div>
                <div style={{ height: 8, background: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 6,
                    background: "linear-gradient(90deg, #1e3a5f, #2d5986)",
                    width: `${(attempted / total) * 100}%`,
                  }} />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13, color: "#475569" }}>
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>⏱ Time Analysis</div>
                  <div>Total time: {fmtTime(timeTaken)}</div>
                  <div>Avg per question: {avgTime}s</div>
                  <div>Remaining: {fmtTime(QUIZ_DURATION - timeTaken)}</div>
                </div>
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>🎯 Accuracy</div>
                  <div>Attempted: {attempted}</div>
                  <div>Correct: {correct} ({attempted > 0 ? Math.round((correct / attempted) * 100) : 0}%)</div>
                  <div>Unanswered: {total - attempted}</div>
                </div>
              </div>
            </div>
          )}

          {tab === "breakdown" && (
            <div>
              <h3 style={{ fontFamily: "Georgia, serif", color: "#0f172a", margin: "0 0 16px", fontSize: 16 }}>
                Performance by Question Type
              </h3>
              {Object.entries(byType).map(([type, data]) => {
                const pct = data.answered > 0 ? Math.round((data.correct / data.answered) * 100) : 0;
                const c = TYPE_COLORS[type];
                return (
                  <div key={type} style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <TypeBadge type={type} />
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          {data.correct}/{data.answered} of {data.total} questions
                        </span>
                      </div>
                      <span style={{ fontFamily: "Georgia, serif", fontWeight: "bold", fontSize: 16, color: c.text }}>
                        {pct}%
                      </span>
                    </div>
                    <div style={{ height: 8, background: "#f1f5f9", borderRadius: 6, overflow: "hidden" }}>
                      <div style={{
                        height: "100%", borderRadius: 6,
                        background: c.text, width: `${pct}%`, transition: "width 1s ease",
                      }} />
                    </div>
                  </div>
                );
              })}
              <div style={{
                marginTop: 16, background: "#f8fafc", borderRadius: 10, padding: "12px 14px",
                fontSize: 13, color: "#475569", border: "1px solid #e2e8f0",
              }}>
                <div style={{ fontWeight: 600, color: "#0f172a", marginBottom: 6 }}>📌 Study Focus</div>
                {Object.entries(byType)
                  .filter(([, d]) => d.answered > 0 && (d.correct / d.answered) < 0.6)
                  .map(([type]) => (
                    <div key={type} style={{ marginBottom: 4 }}>
                      ⚠️ <strong>{TYPE_LABELS[type]}</strong> — below 60% — recommend review
                    </div>
                  ))}
                {Object.entries(byType).every(([, d]) => d.answered === 0 || (d.correct / d.answered) >= 0.6) && (
                  <div>✓ All attempted categories are at or above 60%</div>
                )}
              </div>
            </div>
          )}

          {tab === "review" && (
            <div>
              <h3 style={{ fontFamily: "Georgia, serif", color: "#0f172a", margin: "0 0 4px", fontSize: 16 }}>
                Incorrect Answers Review
              </h3>
              {missedQs.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: "#64748b" }}>
                  <div style={{ fontSize: 36 }}>🎉</div>
                  <div style={{ marginTop: 10, fontWeight: 600 }}>No incorrect answers to review!</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
                    {reviewIdx + 1} of {missedQs.length} missed questions
                  </div>
                  <div style={{
                    border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", marginBottom: 14,
                  }}>
                    <QuestionCard
                      question={missedQs[reviewIdx]}
                      selectedAnswer={answers[QUESTIONS.indexOf(missedQs[reviewIdx])]}
                      onSelect={() => {}}
                      showResult={true}
                    />
                    <div style={{ padding: "0 24px 16px" }} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => reviewIdx > 0 && setReviewIdx(reviewIdx - 1)}
                      disabled={reviewIdx === 0}
                      style={{
                        flex: 1, background: reviewIdx === 0 ? "#f1f5f9" : "#f8fafc",
                        color: reviewIdx === 0 ? "#94a3b8" : "#1e3a5f",
                        border: "1px solid #e2e8f0", borderRadius: 8,
                        padding: "10px", fontSize: 13, fontWeight: 600, cursor: reviewIdx === 0 ? "default" : "pointer",
                      }}
                    >
                      ← Prev
                    </button>
                    <button
                      onClick={() => reviewIdx < missedQs.length - 1 && setReviewIdx(reviewIdx + 1)}
                      disabled={reviewIdx === missedQs.length - 1}
                      style={{
                        flex: 1,
                        background: reviewIdx === missedQs.length - 1 ? "#f1f5f9" : "linear-gradient(135deg, #1e3a5f, #2d5986)",
                        color: reviewIdx === missedQs.length - 1 ? "#94a3b8" : "#fff",
                        border: "none", borderRadius: 8, padding: "10px",
                        fontSize: 13, fontWeight: 600,
                        cursor: reviewIdx === missedQs.length - 1 ? "default" : "pointer",
                      }}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <button onClick={onRestart} style={{
            flex: 1, minWidth: 140,
            background: "linear-gradient(135deg, #1e3a5f, #2d5986)",
            color: "#fff", border: "none", borderRadius: 10,
            padding: "13px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}>
            🔄 New Quiz
          </button>
          <button onClick={onHome} style={{
            flex: 1, minWidth: 140,
            background: "#fff", color: "#1e3a5f",
            border: "1px solid #e2e8f0", borderRadius: 10,
            padding: "13px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer",
          }}>
            ← Home
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("home"); // home | practice | quiz

  if (screen === "practice") {
    return <PracticeMode onHome={() => setScreen("home")} />;
  }
  if (screen === "quiz") {
    return <TimedQuizMode onHome={() => setScreen("home")} />;
  }
  return <HomeScreen onMode={setScreen} />;
}
